import { useLocalStorage } from "@mantine/hooks";
import {
	type FormAsyncValidateOrFn,
	type FormOptions,
	type FormValidateOrFn,
	useForm,
} from "@tanstack/react-form";
import { useEffect, useRef } from "react";

export function usePersistentForm<
	TFormData,
	TOnMount extends undefined | FormValidateOrFn<TFormData>,
	TOnChange extends undefined | FormValidateOrFn<TFormData>,
	TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
	TOnBlur extends undefined | FormValidateOrFn<TFormData>,
	TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
	TOnSubmit extends undefined | FormValidateOrFn<TFormData>,
	TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>,
	TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>,
	TSubmitMeta,
>(
	key: string,
	formArgs: FormOptions<
		TFormData,
		TOnMount,
		TOnChange,
		TOnChangeAsync,
		TOnBlur,
		TOnBlurAsync,
		TOnSubmit,
		TOnSubmitAsync,
		TOnServer,
		TSubmitMeta
	>,
) {
	const [formState, setFormState] = useLocalStorage({
		key,
		defaultValue: formArgs.defaultValues,
	});

	const form = useForm({
		...formArgs,
		defaultValues: formState,
	});

	const isSubmittedRef = useRef(false);

	useEffect(() => {
		// This method causes 5 store updates
		return form.store.subscribe((state) => {
			setFormState(state.currentVal.values);
		});
	}, [form.store, setFormState]);

	// Track isSubmitted state
	useEffect(() => {
		isSubmittedRef.current = form.state.isSubmitted;
	}, [form.state.isSubmitted]);

	// clear form state on unmount and submit
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		return () => {
			if (isSubmittedRef.current) {
				window.localStorage.removeItem(key);
			}
		};
	}, []);

	return form;
}
